import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { Icon } from '@iconify/react'
import Card from '../components/common/Card'
import PageContainer from '../components/layout/PageContainer'

const Analytics = () => {
  // Compliance trends data
  const complianceTrendsData = [
    { week: 'Week 1', compliance: 78, critical: 25, fixed: 145 },
    { week: 'Week 2', compliance: 82, critical: 20, fixed: 234 },
    { week: 'Week 3', compliance: 85, critical: 15, fixed: 456 },
    { week: 'Week 4', compliance: 87, critical: 12, fixed: 1247 },
    { week: 'Week 5', compliance: 97, critical: 12, fixed: 1047 },
    { week: 'Week 6', compliance: 90, critical: 12, fixed: 947 },
  ]

  // Issue categories data
  const issueCategoriesData = [
    { name: 'Images', value: 89, color: '#0ea5e9' },
    { name: 'Forms', value: 45, color: '#0c4a6e' },
    { name: 'Navigation', value: 32, color: '#3b82f6' },
    { name: 'Color Contrast', value: 78, color: '#10b981' },
    { name: 'Headings', value: 54, color: '#f59e0b' },
    { name: 'Other', value: 44, color: '#ef4444' },
  ]

  // Deployment history data
  const deploymentData = [
    { day: 'Mon', successful: 45, failed: 5 },
    { day: 'Tue', successful: 52, failed: 3 },
    { day: 'Wed', successful: 38, failed: 7 },
    { day: 'Thu', successful: 65, failed: 4 },
    { day: 'Fri', successful: 73, failed: 2 },
    { day: 'Sat', successful: 48, failed: 6 },
    { day: 'Sun', successful: 42, failed: 3 },
  ]

  const COLORS = ['#0ea5e9', '#0c4a6e', '#3b82f6', '#10b981', '#f59e0b', '#ef4444']

  const wcagCompliance = [
    { level: 'Level A', percentage: 95 },
    { level: 'Level AA', percentage: 87 },
    { level: 'Level AAA', percentage: 62 },
  ]

  const issuesBySeverity = [
    { severity: 'Critical', count: 12, icon: 'mdi:alert-circle', color: 'bg-danger/20 text-danger' },
    { severity: 'High', count: 45, icon: 'mdi:alert-triangle', color: 'bg-warning/20 text-warning' },
    { severity: 'Medium', count: 128, icon: 'mdi:alert', color: 'bg-orange-100 text-orange-600' },
    { severity: 'Low', count: 157, icon: 'mdi:information', color: 'bg-info/20 text-info' },
  ]

  return (
    <PageContainer title="Analytics" subtitle="Deep insights into your accessibility performance">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6 mb-6">
        <Card title="WCAG Compliance by Level">
          <div className="space-y-4">
            {wcagCompliance.map((item) => (
              <div key={item.level} className="flex items-center gap-3">
                <span className="font-semibold min-w-[80px]">{item.level}</span>
                <div className="flex-1 h-3 bg-bg-light rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-primary-dark rounded-full transition-all"
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
                <span className="font-semibold min-w-[50px] text-right">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Issues by Severity">
          <div className="space-y-2">
            {issuesBySeverity.map((item) => (
              <div
                key={item.severity}
                className={`flex items-center gap-3 p-3 rounded-lg font-semibold ${item.color}`}
              >
                <Icon icon={item.icon} className="text-xl" />
                <span className="flex-1">{item.severity}</span>
                <strong className="text-xl">{item.count}</strong>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6 mb-6">



        <Card title="Deployment History">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={deploymentData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="successful" stackId="a" fill="#10b981" name="Successful Deployments" />
              <Bar dataKey="failed" stackId="a" fill="#ef4444" name="Failed Deployments" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Issue Categories">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={issueCategoriesData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {issueCategoriesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Compliance Trends">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={complianceTrendsData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="week" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="compliance"
              stroke="#0ea5e9"
              strokeWidth={2}
              name="Overall Compliance"
            />
            <Line
              type="monotone"
              dataKey="critical"
              stroke="#ef4444"
              strokeWidth={2}
              name="Critical Issues"
            />
            <Line
              type="monotone"
              dataKey="fixed"
              stroke="#10b981"
              strokeWidth={2}
              name="Fixed Issues"
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </PageContainer>
  )
}

export default Analytics
